import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  TextField,
  Grid,
  Paper,
  Box,
  Typography,
  CircularProgress,
  MenuItem,
  styled,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../store";
import {
  putSystem,
  postSystem,
} from "../../../../../features/slices/SystemRegistry";
import Back from "../../../../../components/Back";
import { useNavigate, useParams } from "react-router-dom";
import { fetchSystem } from "../../../../../features/slices/SystemRegistry";
import Title from "../../../../../components/Title";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNotification } from "../../../../../context/NotificationContext";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  repo: Yup.string().required("Repository URL is required"),
  reg_type: Yup.string().required("Registry type is required"),
  entity_type: Yup.string().required("Entity type is required"),
  username: Yup.string()
    .nullable()
    .test("is-required", "Username is required", function (value) {
      return this.parent.reg_type === "ssh_key" ? true : !!value;
    }),
  token: Yup.string()
    .nullable()
    .test("is-required", "Token is required", function (value) {
      return this.parent.reg_type === "ssh_key" ? true : !!value;
    }),
});

interface SystemFormData {
  id?: number;
  title: string;
  repo: string;
  reg_type: string;
  username?: string;
  token?: string;
  entity_type: string;
  file?: File | null;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const SystemRegistryCreateOrUpdate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id, type } = useParams<{ id: string; type: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const { notify } = useNotification();
  const [initialValues, setInitialValues] = useState<SystemFormData>({
    title: "",
    repo: "",
    reg_type: "",
    username: "",
    token: "",
    entity_type: "",
    file: null,
  });

  const loadSystem = useCallback(async () => {
    const query = new URLSearchParams({
      reg_id: id,
      entity_type: type,
    }).toString();
    try {
      const response = await dispatch(fetchSystem(query)).unwrap();
      if (response) {
        setInitialValues({
          id: response.id,
          title: response.title,
          repo: response.repo,
          reg_type: response.reg_type,
          username: response.reg_value.username,
          token: response.reg_value.token,
          entity_type: response.entity_type,
          file: null,
        });
      }
    } catch (error) {
      notify("Failed to fetch system", "error");
      console.error("Failed to fetch system", error);
    }
  }, [id, type, dispatch, notify]);

  useEffect(() => {
    if (id && type) {
      loadSystem();
    }
  }, [loadSystem, id, type]);

  const onSubmit = async (values: SystemFormData) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("repo", values.repo);
    formData.append("reg_type", values.reg_type);
    formData.append("entity_type", values.entity_type);

    if (id) {
      formData.append("reg_id", id.toString());
    }
    if (values.reg_type !== "ssh_key") {
      const regValue = JSON.stringify({
        username: values.username,
        token: values.token,
      });
      formData.append("reg_value", regValue);
    }
    if (values.reg_type === "ssh_key" && values.file) {
      formData.append("file", values.file);
    }

    try {
      if (id) {
        await dispatch(putSystem(formData)).unwrap();
        notify("System updated successfully", "success");
      } else {
        await dispatch(postSystem(formData)).unwrap();
        notify("System created successfully", "success");
      }
      navigate(`/management/system-registry`);
    } catch (error) {
      notify("Failed to submit form", "error");
      console.error("Error submitting form", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Back />
      <Title>{id ? "Edit System" : "Create New System"}</Title>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ errors, touched, isSubmitting, setFieldValue, values }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    name="title"
                    label="System Title"
                    fullWidth
                    error={touched.title && !!errors.title}
                    helperText={<ErrorMessage name="title" />}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    name="repo"
                    label="Repository URL"
                    fullWidth
                    error={touched.repo && !!errors.repo}
                    helperText={<ErrorMessage name="repo" />}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    select
                    name="reg_type"
                    label="Registry Type"
                    fullWidth
                    error={touched.reg_type && !!errors.reg_type}
                    helperText={<ErrorMessage name="reg_type" />}
                  >
                    <MenuItem value="token">Token</MenuItem>
                    <MenuItem value="ssh_key">SSH Key</MenuItem>
                  </Field>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    select
                    name="entity_type"
                    label="Entity Type"
                    fullWidth
                    error={touched.entity_type && !!errors.entity_type}
                    helperText={<ErrorMessage name="entity_type" />}
                  >
                    <MenuItem value="cases">Cases</MenuItem>
                    <MenuItem value="framework">Framework</MenuItem>
                    <MenuItem value="resources">Resources</MenuItem>
                  </Field>
                </Grid>
                {values.reg_type !== "ssh_key" && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        as={TextField}
                        name="username"
                        label="Registry Username"
                        fullWidth
                        error={touched.username && !!errors.username}
                        helperText={<ErrorMessage name="username" />}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        as={TextField}
                        name="token"
                        label="Registry Token"
                        fullWidth
                        error={touched.token && !!errors.token}
                        helperText={<ErrorMessage name="token" />}
                      />
                    </Grid>
                  </>
                )}

                {values.reg_type === "ssh_key" && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">
                      SSH Key:{" "}
                      {values.file ? values.file.name : "No file selected"}
                    </Typography>

                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload SSH Key
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(event: any) =>
                          setFieldValue("file", event.target.files?.[0])
                        }
                      />
                    </Button>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || isLoading}
                      startIcon={isLoading && <CircularProgress size={20} />}
                    >
                      {id ? "Update System" : "Create System"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </>
  );
};

export default SystemRegistryCreateOrUpdate;
